/**
 * useMedicalAnalytics.js
 *
 * Analytics hook for the Assessment Wizard's Screen 6 ("Medical
 * Information"). Mirrors useLifestyleAnalytics.js / useActivityAnalytics.js
 * exactly — same GA4 wrapper via react-ga4, same IS_PRODUCTION guard, same
 * dev console.group/table logging.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[MedicalAnalytics] ${eventName}`, "color:#C62828;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

const useMedicalAnalytics = () => {
  const trackStepView = useCallback((sessionId) => {
    send("medical_step_view", { session_id: sessionId, step: 6 });
  }, []);

  const trackConditionToggle = useCallback((sessionId, conditionId, selected) => {
    send("medical_condition_toggle", {
      session_id: sessionId,
      condition: conditionId,
      selected,
    });
  }, []);

  const trackMedicationsInput = useCallback((sessionId, length) => {
    send("medical_medications_input", { session_id: sessionId, length });
  }, []);

  const trackRiskFlagSet = useCallback((sessionId, riskConditions = []) => {
    send("medical_risk_flag_set", {
      session_id: sessionId,
      // Conditions driving the professional-review notice: any
      // professional_review-tier selection, or 2+ caution/
      // professional_review conditions combined (FSD v2.1 §5).
      risk_conditions: riskConditions.join(","),
    });
  }, []);

  // Distinct from trackRiskFlagSet — this fires when a BLOCK-tier
  // condition (currently eating-disorder concern) is selected, per
  // DEC-17 / §5 High-Risk Blocking Logic. Plan generation must not
  // produce a personalized weight-loss/calorie-deficit plan for this
  // user; kept separate from the general risk-flag event so product/QA
  // can track the two safety paths independently.
  const trackPlanBlockFlagSet = useCallback((sessionId, blockingConditions = []) => {
    send("medical_plan_block_flag_set", {
      session_id: sessionId,
      blocking_conditions: blockingConditions.join(","),
    });
  }, []);

  const trackStepNext = useCallback((sessionId, data = {}, riskFlag, planBlocked) => {
    send("medical_step_next", {
      session_id: sessionId,
      step: 6,
      conditions: Array.isArray(data.conditions) ? data.conditions.join(",") : undefined,
      has_medications: !!data.medications,
      risk_flag: riskFlag,
      plan_blocked: planBlocked,
    });
  }, []);

  const trackStepPrevious = useCallback((sessionId) => {
    send("medical_step_previous", { session_id: sessionId, step: 6 });
  }, []);

  const trackValidationBlocked = useCallback((sessionId, errors = {}) => {
    send("medical_validation_blocked", {
      session_id: sessionId,
      missing_fields: Object.keys(errors).join(","),
    });
  }, []);

  return {
    trackStepView,
    trackConditionToggle,
    trackMedicationsInput,
    trackRiskFlagSet,
    trackPlanBlockFlagSet,
    trackStepNext,
    trackStepPrevious,
    trackValidationBlocked,
  };
};

export default useMedicalAnalytics;